import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { ChangeEvent } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import { DEAL_SEARCH_BY_FIELD, SEARCH_FIELDS } from '../../../../util/deals-util';
import ColumnVisibilityButton, { ColumnKey } from './ColumnVisibilityButton';

type DealsTableSearchInputProps = {
  search: string;
  onSearchStringChange: (value: string, searchByField: DEAL_SEARCH_BY_FIELD) => void;
  searchByColumns: DEAL_SEARCH_BY_FIELD;
  parentComponent: string;
  visibleColumns: ColumnKey[];
  onColumnVisibilityChange: (columns: ColumnKey[]) => void;
};

const DealsTableSearchInput = ({
  search,
  onSearchStringChange,
  searchByColumns,
  parentComponent,
  visibleColumns,
  onColumnVisibilityChange,
}: DealsTableSearchInputProps) => {

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newField = e.target.value as DEAL_SEARCH_BY_FIELD;
    onSearchStringChange(search, newField);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSearchStringChange(e.target.value, searchByColumns);
  };

  const getSearchLabel = () => {
    const field = SEARCH_FIELDS.find(f => f.value === searchByColumns);
    return field?.label || 'field' ;
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'center',
        flexWrap: 'wrap',
        mb: 2
      }}
    >
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 500,
          color: 'text.secondary'
        }}
      >
        Search by:
      </Typography>
      
      <TextField
        id="search-by-field"
        select
        size="small"
        variant="outlined"
        value={searchByColumns}
        onChange={handleFieldChange}
        SelectProps={{
          renderValue: (value) => {
            const field = SEARCH_FIELDS.find(f => f.value === value);
            return field?.label || '';
          }
        }}
        sx={{ 
          minWidth: 220,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F5F5F5'
          }
        }}
      >
        {SEARCH_FIELDS.map((option) => (
          <MenuItem 
            key={option.value} 
            value={option.value}
            sx={{
              backgroundColor: option.value === searchByColumns ? '#F5F5F5' : 'transparent',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: option.value === searchByColumns ? '#F5F5F5' : undefined
              },
              '& .checkmark-icon': {
                display: 'inline-flex'
              }
            }}
          >
            <Box component="span" sx={{ flexGrow: 1 }}>
              {option.label}
            </Box>
            {option.value === searchByColumns && (
              <CheckIcon className="checkmark-icon" sx={{ color: 'primary.main', ml: 2 }} fontSize="small" />
            )}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        id={`${parentComponent}-search`}
        placeholder={`Search by ${getSearchLabel()}`}
        size="small"
        value={search}
        onChange={handleSearchChange}
        variant="outlined"
        sx={{ 
          flexGrow: 1,
          minWidth: 325,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F5F5F5',
            '&:hover': {
              '& .MuiSvgIcon-root': {
                color: 'primary.main'
              }
            }
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {search && (
                <IconButton 
                  size="small"
                  onClick={() => onSearchStringChange('', searchByColumns)}
                  sx={{ p: 0.5, mr: 0.5 }}
                >
                  <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              )}
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ shrink: false }}
      />

      <ColumnVisibilityButton
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={onColumnVisibilityChange}
      />
    </Box>
  );
};

export default DealsTableSearchInput;
